import { Grid, makeStyles } from '@material-ui/core';
import arrayMove from 'array-move';
import { Body } from 'copilot-design-system';
import { sortBy } from 'lodash';
import React from 'react';
import { SortEnd } from 'react-sortable-hoc';

import * as Constants from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { CustomFieldsMenu } from 'src/legacy/components/CustomFieldsMenu';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import {
  useCustomFields,
  useGetCustomFieldsQuery,
  useUpdateCustomFieldsMutation,
} from 'src/services/api/customFieldsApi';
import {
  Client,
  Company,
  CustomFieldEntityType,
  CustomFieldOperationType,
  CustomFieldOption,
  MultiSelectCustomField,
} from 'src/store/clients/types';
import { alertSnackbar } from 'src/store/ui/actions';
import { isCustomFieldUsedInAppVisibility } from 'src/utils/app_visibility';
import { hasOperation } from 'src/utils/UserUtils';

import { SortableCustomPropertiesList } from './SortableCustomPropertiesList';

const useStyles = makeStyles(() => ({
  sortableListBox: {
    width: '100%',
  },
}));

// these props are used to pass a custom anchor for the custom fields menu
// e.g. in the new right sidebar design, the custom fields menu is anchored
// to the plus button showing in the properties section header.
type CustomAnchorProps = {
  setCustomFieldMenuAnchor?: (anchor: HTMLElement | null) => void;
  customFieldMenuAnchor?: HTMLElement | null;
};

type CustomPropertiesProps = {
  currentClient?: Client;
  currentCompany?: Company;
};

export const CustomPropertiesContainer: React.FC<
  CustomPropertiesProps & CustomAnchorProps
> = ({
  currentClient: clientUser,
  currentCompany: company,
  setCustomFieldMenuAnchor,
  customFieldMenuAnchor,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const entityType = clientUser
    ? CustomFieldEntityType.CLIENT
    : CustomFieldEntityType.COMPANY;
  const clientCustomFields = useCustomFields(entityType);
  const { data: clientCustomFieldsEntity } = useGetCustomFieldsQuery({
    type: entityType,
  });

  const [updateCustomFields] = useUpdateCustomFieldsMutation();

  const { permissions } = useAppSelector((state: any) => ({
    permissions: state.user.data?.permissions,
  }));

  const canUpdateCustomFields = hasOperation(
    Constants.PERM_MOD_CUSTOM_FIELDS,
    Constants.CUSTOM_FIELD_OPERATIONS.CHANGE_TEMPLATE,
    permissions,
  );

  const customFieldMap = clientCustomFields;

  const { data: { moduleSettings } = {} } = useGetInstallsQuery();

  const [anchoredCustomFieldId, setAnchoredCustomFieldId] = React.useState<
    string | null
  >(null);

  const [customFields, setCustomFields] = React.useState<
    CustomFieldOption[] | null
  >(null);

  const [propertiesMenuAnchor, setPropertiesMenuAnchor] =
    React.useState<HTMLElement | null>(null);

  const [updatingCustomFieldOption, setUpdatingCustomFieldOption] =
    React.useState<CustomFieldOption | null>(null);

  React.useEffect(() => {
    if (!customFieldMap) {
      return;
    }
    setCustomFields(sortBy(Object.values(customFieldMap), 'order'));
  }, [customFieldMap]);

  const openUpdateCustomFieldMenu = (
    ev: React.MouseEvent<HTMLElement, MouseEvent>,
    updatingField: MultiSelectCustomField | CustomFieldOption,
  ) => {
    setUpdatingCustomFieldOption(updatingField);
    setPropertiesMenuAnchor(ev.currentTarget);
  };

  /**
   * if the index is unchanged, we trigger the edit custom property flow with popover
   * if the index is changed, we change the order property of the custom properties and save to DB
   * @param swap SortEnd object containing HTML nodes and incides after a reordering operation
   */
  const handleCustomPropertySwap = async (swap: SortEnd) => {
    if (swap.oldIndex === swap.newIndex && customFields) {
      return;
    }
    if (!customFieldMap) return;
    const sortedCustomFields = sortBy(Object.values(customFieldMap), 'order');
    const newCustomFields = arrayMove(
      sortedCustomFields,
      swap.oldIndex,
      swap.newIndex,
    );

    const additionalFields: Record<string, CustomFieldOption> = {};
    newCustomFields.forEach((f, arrayIndex) => {
      additionalFields[f.id] = {
        ...f,
        order: arrayIndex,
      };
    });

    if (!clientCustomFieldsEntity) {
      return;
    }
    await updateCustomFields({
      customFields: {
        ...clientCustomFieldsEntity,
        additionalFields,
      },
      type: entityType,
    })
      .unwrap()
      .catch((error) => {
        console.error('Failed to update custom fields order:', error);
        // Optionally revert the local state change
        setCustomFields(customFields);
      });
  };

  /**
   * This function updates the map of custom fields after an insert/update
   * "order" is also recalculated and applied to each of the fields
   * @param savedCustomField
   * @param deletedOptionKey
   * @param operationType
   */
  const handleSaveProperty = async (
    savedCustomField: CustomFieldOption | null,
    deletedOptionKey?: string,
    operationType?: CustomFieldOperationType,
  ): Promise<void> => {
    if (!savedCustomField) {
      return;
    }

    const updatedAdditionalFields: Record<string, CustomFieldOption> = {};
    const currentFields = sortBy(Object.values(customFieldMap), 'order');

    const isNewField = !customFieldMap[savedCustomField.id];
    if (isNewField) {
      // only append new custom fields to this list, not updating fields
      currentFields.push(savedCustomField);
    }
    // for each custom field we append either the existing custom field
    currentFields.forEach((cf, idx) => {
      updatedAdditionalFields[cf.id] =
        !isNewField && cf.id === savedCustomField.id
          ? {
              ...savedCustomField,
              order: idx,
            }
          : {
              ...cf,
              order: idx,
            };
    });

    if (moduleSettings && deletedOptionKey) {
      const [isUsed, moduleInUse] = isCustomFieldUsedInAppVisibility(
        moduleSettings,
        deletedOptionKey,
        (rule, deletedCustomField) =>
          rule.ruleType === Constants.VisibilityRuleType.CustomField &&
          rule.values.includes(deletedCustomField),
      );
      if (isUsed) {
        dispatch(
          alertSnackbar({
            errorMessage:
              'This option is being used in visibility config for ' +
              moduleInUse,
          }),
        );
        return;
      }
    }

    setAnchoredCustomFieldId(null);

    if (!clientCustomFieldsEntity) {
      const error = new Error('Client custom fields entity not available');
      throw error;
    }

    try {
      await updateCustomFields({
        customFields: {
          ...clientCustomFieldsEntity,
          additionalFields: updatedAdditionalFields,
        },
        type: entityType,
        operationType,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update custom fields:', error);
      throw error;
    }
  };

  /**
   * This function calls the update custom fields action but
   *  with a map containing only deleted custom fields
   * @param deletedCustomField
   */
  const handleDeleteProperty = async (
    deletedCustomField: CustomFieldOption,
  ) => {
    if (!moduleSettings) {
      return;
    }

    const [isUsed, moduleInUse] = isCustomFieldUsedInAppVisibility(
      moduleSettings,
      deletedCustomField.id,
      (rule, deletedCustomFieldId) =>
        rule.ruleType === Constants.VisibilityRuleType.CustomField &&
        rule.customFieldId === deletedCustomFieldId,
    );

    if (isUsed) {
      dispatch(
        alertSnackbar({
          errorMessage:
            'This option is being used in visibility config for ' + moduleInUse,
        }),
      );
      return;
    }

    // Remove the deleted field from additionalFields
    const updatedAdditionalFields = {
      ...clientCustomFields,
    };
    delete updatedAdditionalFields[deletedCustomField.id];

    if (!clientCustomFieldsEntity) {
      return;
    }

    await updateCustomFields({
      customFields: {
        ...clientCustomFieldsEntity,
        additionalFields: updatedAdditionalFields,
      },
      type: entityType,
    });
  };

  const handleCloseMenu = () => {
    setCustomFieldMenuAnchor?.(null);
    setPropertiesMenuAnchor(null);
    setUpdatingCustomFieldOption(null);
  };

  return (
    <div className={classes.sortableListBox}>
      {customFields?.length === 0 ? (
        <Body className="text-gray-small text-center">
          {canUpdateCustomFields
            ? ' Click + to add custom field'
            : 'No custom fields'}
        </Body>
      ) : (
        <SortableCustomPropertiesList
          setPropertiesMenuAnchor={setPropertiesMenuAnchor}
          anchoredCustomFieldId={anchoredCustomFieldId}
          openUpdateCustomFieldMenu={openUpdateCustomFieldMenu}
          onSortEnd={handleCustomPropertySwap}
          onSaveProperty={handleSaveProperty}
          clientUser={clientUser}
          company={company}
          customFields={customFields}
          distance={1}
        />
      )}
      {canUpdateCustomFields && (
        <Grid item xs={12}>
          <CustomFieldsMenu
            updatingCustomFieldOption={updatingCustomFieldOption}
            anchorEl={customFieldMenuAnchor ?? propertiesMenuAnchor}
            onClose={handleCloseMenu}
            onSave={handleSaveProperty}
            onDelete={handleDeleteProperty}
            company={company}
            client={clientUser}
          />
        </Grid>
      )}
    </div>
  );
};
